import { useEffect, useState } from 'react';

import { type ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Button, Stack } from '@mui/material';
import { Plus, Target } from 'lucide-react';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type GetLeadsCardQuery,
  type Leads_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { CreateLeadDrawer } from '@modules/_shared/components/create-lead/CreateLeadDrawer';
import { IndicatorLeadStage } from '@modules/_shared/components/IndicatorLeadStage';
import { LeadUpdatedTimeAgo } from '@modules/_shared/components/lead/LeadUpdatedTimeAgo';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  getHorizonLabel,
  translateAppraisalReason,
} from '@modules/_shared/utils/lead-labels';
import { LeadDetailsDrawer } from '@modules/lead-details/LeadDetailsDrawer';
import {
  LeadCardRow,
  SubtitleTruncatedItem,
} from '@modules/users/user-overview/components/LeadCardRow';
import { BUY_APPRAISAL_REASONS } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';

const GET_LEADS_CARD = gql(/* GraphQL */ `
  query GetLeadsCard($userId: uuid!, $where: leads_bool_exp!) {
    leads(
      where: { _and: [{ contact_id: { _eq: $userId } }, $where] }
      order_by: { requalified_or_created_at: desc }
      limit: 100
    ) {
      id
      ...LeadUpdatedTimeAgo
      appraisal_reason
      sale_horizon
      buy_horizon
      property {
        route
        street_number
        postcode
        locality
        state
        country
      }
      lead_agents(
        where: { passed_at: { _is_null: true } }
        limit: 1
        order_by: { created_at: desc }
      ) {
        id
        lead_stage {
          id
          label
          status
        }
      }
    }
    leads_aggregate(
      where: { _and: [{ contact_id: { _eq: $userId } }, $where] }
    ) {
      aggregate {
        count
      }
    }
  }
`);

type Lead = NonNullable<GetLeadsCardQuery['leads'][number]>;

type LeadListItemProps = {
  lead: Lead;
};

const LeadListItem = ({ lead }: LeadListItemProps) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();

  const leadStage = lead.lead_agents[0]?.lead_stage;
  const stageLabel =
    leadStage?.label ?? (lead.requalified_at ? t('Requalified') : t('New'));
  const stageStatus = leadStage?.status ?? 'active';

  const reasonLabel =
    getHorizonLabel(t, {
      appraisalReason: lead.appraisal_reason,
      saleHorizon: lead.sale_horizon,
      buyHorizon: lead.buy_horizon,
    }) ?? translateAppraisalReason(t, lead.appraisal_reason);

  const address = lead.property ? formatAddress(lead.property, ', ') : null;

  return (
    <LeadCardRow
      icon={<Target size={18} />}
      color="primary"
      to={{
        pathname: `leads/${lead.id}`,
        search: searchParams.toString(),
      }}
      date={
        <LeadUpdatedTimeAgo lead={lead} showBothDate={me?.is_admin ?? false} />
      }
      title={address || reasonLabel || t('No address')}
      subtitle={
        <>
          <Box component="span" sx={{ flexShrink: 0 }}>
            <IndicatorLeadStage status={stageStatus}>
              {stageLabel}
            </IndicatorLeadStage>
          </Box>
          {reasonLabel && (
            <SubtitleTruncatedItem>{reasonLabel}</SubtitleTruncatedItem>
          )}
        </>
      }
    />
  );
};

export type UserLeadsCardProps = {
  userId: string;
  variant?: 'selling' | 'buying';
  hideWhenEmpty?: boolean;
  defaultExpanded?: boolean;
};

export const UserLeadsCard = ({
  userId,
  variant = 'selling',
  hideWhenEmpty = false,
  defaultExpanded = false,
}: UserLeadsCardProps) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);

  const where: Leads_Bool_Exp =
    variant === 'buying'
      ? { appraisal_reason: { _in: [...BUY_APPRAISAL_REASONS] } }
      : { appraisal_reason: { _nin: [...BUY_APPRAISAL_REASONS] } };

  const { data, loading, error, refetch } = useQuery(GET_LEADS_CARD, {
    variables: { userId, where },
  });

  useEffect(() => {
    if (error) {
      setApolloError(error);
    }
  }, [error]);

  const leads = data?.leads ?? [];
  const count = data?.leads_aggregate?.aggregate?.count ?? 0;

  if (hideWhenEmpty && !loading && count === 0) {
    return (
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    );
  }

  return (
    <>
      <ListingAccordion
        title={
          <>
            {t('Valuations')}
            {count > 0 && <CountBadge count={count} />}
          </>
        }
        defaultExpanded={defaultExpanded}
        standalone
      >
        <Stack sx={{ gap: 0.5 }}>
          {leads.map(lead => (
            <LeadListItem key={lead.id} lead={lead} />
          ))}
          {variant === 'selling' && (
            <Button
              fullWidth
              variant="soft"
              color="primary"
              startIcon={<Plus />}
              onClick={() => setDrawerOpen(true)}
              sx={{ mt: leads.length > 0 ? 1 : 0 }}
            >
              {t('Add seller lead')}
            </Button>
          )}
        </Stack>
      </ListingAccordion>

      {drawerOpen && (
        <CreateLeadDrawer
          onClose={() => setDrawerOpen(false)}
          onCreate={leadId => {
            setDrawerOpen(false);
            navigate({
              pathname: `leads/${leadId}`,
              search: searchParams.toString(),
            });
            refetch();
          }}
          userId={userId}
        />
      )}

      <Routes>
        <Route path="leads/:leadId/*" element={<LeadDetailsDrawer />} />
      </Routes>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    </>
  );
};
